import React from "react";
import * as palette from "@govlaunch/palette";
import { FormattedMessage } from "react-intl";
import { IStripePlanFieldsFragment } from "~/lib/queries/fragments/__generated__/StripePlanFields.generated";
import { IPlanRecurrency } from "~/types/types";
import PlanOfferingsBlock from "~/components/makers/PlanOfferingsBlock";
import PlanOfferings from "~/components/makers/PlanOfferings";
import { CheckEmoji, StarEmoji } from "~/components/makers/Emojis";
import Strongify from "~/lib/utils/strongify";

interface IToPlanOptionProps {
  toPlan: IStripePlanFieldsFragment;
}

export default function ToPlanOption({ toPlan }: IToPlanOptionProps) {
  return (
    <div
      css={{
        padding: 20,
        border: `solid 1px ${palette.lightestGray}`,
        borderRadius: 4,
      }}
    >
      <div
        css={{
          display: "grid",
          gridRowGap: 12,
        }}
      >
        <strong
          css={{
            color: palette.innovatorBlue,
            fontWeight: "bold",
            fontSize: 16,
            fontStyle: "italic",
            textTransform: "uppercase",
          }}
        >
          <FormattedMessage defaultMessage="New Plan" id="U2j/jE" />
        </strong>

        <h2
          css={{
            margin: 0,
            fontSize: 24,
            color: "#4a4a4a",
          }}
        >
          {toPlan.displayName}
        </h2>

        <h3
          css={{
            margin: 0,
            color: "#797979",
            fontWeight: "bold",
            fontSize: 24,
          }}
        >
          $
          {toPlan.recurrency === IPlanRecurrency.Yearly
            ? (toPlan.price / 100 / 12).toFixed(2)
            : (toPlan.price / 100).toFixed(2)}
          <span
            css={{
              fontSize: 24,
              fontWeight: "normal",
              color: "#9f9f9f",
            }}
          >
            /<FormattedMessage defaultMessage="month" id="Cu3Cty" />
          </span>
        </h3>

        <div
          css={{
            padding: "10px 0px",
          }}
        >
          <PlanOfferingsBlock>
            <PlanOfferings>
              <li>
                {CheckEmoji}{" "}
                <FormattedMessage
                  defaultMessage="Enhanced profiles for <strong>{count} products</strong>"
                  id="oQg1FL"
                  values={{
                    count: toPlan.limits.enrichedProducts,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              {toPlan.limits.productTags > 0 && (
                <li>
                  {CheckEmoji}
                  <FormattedMessage
                    defaultMessage="Up to <strong>{chunk} category tags</strong> per product"
                    id="DQ/iww"
                    values={{
                      chunk: `${toPlan.limits.productTags}`,
                      strong: (chunk: any) => Strongify(chunk),
                    }}
                  />
                </li>
              )}
              <li>
                {CheckEmoji} <FormattedMessage defaultMessage="Enhanced vendor profile" id="ervosm" />
              </li>
              <li>
                {CheckEmoji}{" "}
                <FormattedMessage
                  defaultMessage="Team access for <strong>{count} users</strong>"
                  id="Uj5Y7y"
                  values={{
                    count: toPlan.limits.teamMembers,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              <li>
                {CheckEmoji}
                <FormattedMessage
                  defaultMessage="Add up to <strong>{projectsPerMonth}</strong> new {projectsPerMonth, plural, one {project} other {projects}} per month"
                  id="SkEF2S"
                  values={{
                    projectsPerMonth: toPlan.limits.projectsPerMonth,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              <li>
                {CheckEmoji}
                <FormattedMessage
                  defaultMessage="<strong>{count}</strong>custom branded discussion groups"
                  id="hFfG9q"
                  values={{
                    count: toPlan.limits.groups,
                    strong: (chunk: any) => Strongify(chunk),
                  }}
                />
              </li>
              {toPlan.limits.searchAccess && (
                <li>
                  {CheckEmoji}{" "}
                  <FormattedMessage
                    defaultMessage="View other <strong>product profiles</strong>"
                    id="S0q+nj"
                    values={{ strong: (chunk: any) => Strongify(chunk) }}
                  />
                </li>
              )}
              {toPlan.limits.searchAccess && (
                <li>
                  {CheckEmoji} <FormattedMessage defaultMessage="Search for other vendor & products" id="vEUFjJ" />
                </li>
              )}
              {toPlan.limits.searchAccess && (
                <li>
                  {CheckEmoji} <FormattedMessage defaultMessage="Suggest products to add to Govlaunch" id="MV9qNV" />
                </li>
              )}
              {toPlan.limits.sharingPage && (
                <li>
                  {StarEmoji} <FormattedMessage defaultMessage="Customizable public sharing hub" id="eTjaNG" />
                </li>
              )}
            </PlanOfferings>
          </PlanOfferingsBlock>
        </div>
      </div>
    </div>
  );
}
